import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { APIError, apiClient } from '@/api/client';
import type { InvoiceSend, SendInvoiceInput, SendPreview } from '@jobos/shared';
import { invoiceKeys } from '@/hooks/useInvoices';
import { useToast } from '@/hooks/use-toast';

export const sendKeys = {
  all: ['invoice-sends'] as const,
  byInvoice: (invoiceId: string) => [...sendKeys.all, invoiceId] as const,
  /**
   * Deliberately UNDER byInvoice: a send rewrites `lastSend`, so one prefix
   * invalidation of byInvoice refreshes the log and the preview together.
   */
  preview: (invoiceId: string) => [...sendKeys.byInvoice(invoiceId), 'preview'] as const,
};

/** POST /invoices/:id/send. `invoice` is partial on purpose — sending moves only `sentAt`. */
export interface SendInvoiceResponse {
  send: InvoiceSend;
  invoice: { id: string; sentAt: string };
}

/**
 * What the send dialog opens with (FR-4.4). Returned BARE by the route, unlike
 * the send log below — `SendPreview` is a shared type and wrapping it here
 * would fork it.
 *
 * `enabled` exists because the preview is composed server-side per open: a
 * closed dialog should not be asking the server to draft an email.
 */
export function useSendPreview(
  invoiceId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: sendKeys.preview(invoiceId!),
    queryFn: () => apiClient.get<SendPreview>(`/invoices/${invoiceId}/send-preview`),
    enabled: !!invoiceId && options?.enabled !== false,
    /**
     * ALWAYS FRESH, against the app-wide 5-minute staleTime (App.tsx).
     *
     * This is not a view of data, it is a draft of an email about to leave the
     * building, composed from things a user edits in the next tab along: the
     * client's AP addresses, the manager's email, the invoice's own override
     * and terms. Served from cache, the dialog opens on a draft that was true
     * five minutes ago — adding the missing email to a manager and reopening
     * the dialog showed the same empty recipient list it showed before, with
     * nothing on screen to say the answer was stale.
     */
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * The send log, newest first. The route wraps it as `{ sends }` so the payload
 * can grow; that envelope is transport, so it is unwrapped here and callers see
 * the rows.
 */
export function useInvoiceSends(invoiceId: string | undefined) {
  return useQuery({
    queryKey: sendKeys.byInvoice(invoiceId!),
    queryFn: () =>
      apiClient
        .get<{ sends: InvoiceSend[] }>(`/invoices/${invoiceId}/sends`)
        .then((res) => res.sends),
    enabled: !!invoiceId,
  });
}

/**
 * A send moves three caches, for the same reason a ledger write does
 * (useLedgerInvalidation in useInvoicePayments.ts): the send log gains a row,
 * the invoice's own row crosses Drafts → Sent as `sentAt` lands,
 * the job's rollup counts that invoice differently, and the workspace's own
 * stage lists move the row out of Drafts. Miss one and the workspace shows a
 * stale row beside a fresh send.
 */
function useSendInvalidation(invoiceId: string, jobId: string | undefined) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: sendKeys.byInvoice(invoiceId) });
    queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    // The workspace's stage lists are keyed separately from invoiceKeys, so
    // without this a sent draft stays visibly in Drafts until a manual refresh —
    // the one thing a user watches for after pressing Send. It lives here rather
    // than in an onSent callback because every future caller would otherwise
    // have to remember it, which is how it was missed the first time.
    queryClient.invalidateQueries({ queryKey: ['invoice-workspace'] });
    // Prefix match: covers both the job detail cache and ['jobs', id, 'billing'].
    if (jobId) queryClient.invalidateQueries({ queryKey: ['jobs'] });
  };
}

/**
 * The send route answers with statuses that mean genuinely different things,
 * and every one of those refusals arrives carrying a finished sentence the
 * backend wrote for the user:
 *
 *   409 — refused: a send is in flight, an unresolved attempt has different
 *         content, or the invoice is voided/external.
 *   422 — nobody to send to; the client needs an accounts-payable address.
 *   502 — the provider refused or never answered; nothing was sent.
 *
 * The message survives the wire: `errorHandler` renders an HTTPException as
 * `{ error, status }`, and APIError's constructor takes `data.error` as its
 * `message`. So the reason IS `error.message` — but only when the body's
 * `error` was a string, which is why the fallback exists.
 */
const SEND_ERROR_TITLES: Record<number, string> = {
  409: 'Invoice not sent',
  // 422 covers two different faults — nobody resolved, and a recipient whose
  // domain cannot receive mail — so the title has to be the thing they share.
  // 'No recipients' read as a contradiction over the second one's body, which
  // names the address it is refusing.
  422: 'Check the recipients',
  502: 'Email provider problem',
};

export function describeSendError(error: unknown): { title: string; description: string } {
  const status = error instanceof APIError ? error.status : undefined;
  const raw = error instanceof Error ? error.message : '';
  // zValidator's 400 body puts a ZodError object on `error`, which APIError
  // stringifies into its message. Showing "[object Object]" is worse than
  // showing nothing specific.
  const usable = typeof raw === 'string' && raw.length > 0 && !raw.startsWith('[object ');
  return {
    title: (status !== undefined && SEND_ERROR_TITLES[status]) || 'Error',
    description: usable ? raw : 'The invoice could not be sent.',
  };
}

export function useSendInvoice(invoiceId: string, jobId?: string) {
  const invalidate = useSendInvalidation(invoiceId, jobId);
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: SendInvoiceInput) =>
      apiClient.post<SendInvoiceResponse>(`/invoices/${invoiceId}/send`, data),
    onSuccess: (result) => {
      invalidate();
      toast({ title: 'Invoice sent', description: result.send.recipients.join(', ') });
    },
    onError: (error: Error) => {
      toast({ ...describeSendError(error), variant: 'destructive' });
    },
  });
}
